// Maps an in-shell pathname to its primary workspace section. The subtoolbar crumb
// labels it and the ShellSidePanel renders the section's contextual list from this.
// (The rail in Sidebar.tsx still uses its own active-state matching; migrating it
// onto sectionForPath is a follow-up so crumb/panel/rail share one source.)
//
// `sectionForPath` / `isInShellRoute` now delegate to the single shell-route
// registry (lib/shell-routes.ts), keeping their names so call sites don't churn.

import { routeIsInShell, routeSection } from "./shell-routes";

export type WorkspaceSection =
  | "home"
  | "chat"
  | "docs"
  | "assets"
  | "tasks"
  | "activity"
  | "workflows"
  | "analytics"
  | null;

export function sectionForPath(pathname: string): WorkspaceSection {
  return routeSection(pathname);
}

/**
 * Sections the shell-level ShellSidePanel currently owns. Other sections still
 * render their own page-level secondary content (migrated incrementally),
 * so the shell panel stays collapsed there to avoid a duplicate panel.
 */
export const SHELL_PANEL_SECTIONS: ReadonlySet<WorkspaceSection> = new Set<WorkspaceSection>([
  "home",
  "chat",
  "docs",
  "tasks",
  "activity",
  "workflows",
  "assets",
  "analytics",
]);

/**
 * Shell panels whose content is brand-scoped, so the panel must stay hidden until a brand
 * is selected. Previously expressed inline as `section !== "home" || !!activeBrand`, which
 * silently assumed every FUTURE section was brand-agnostic — workflows is not, and adding
 * it showed brand-scoped queues beside the brand-selection gate.
 *
 * `docs` is absent deliberately: its panel already renders its own empty state.
 */
export const BRAND_SCOPED_PANEL_SECTIONS: ReadonlySet<WorkspaceSection> =
  new Set<WorkspaceSection>(["home", "chat", "tasks", "activity", "workflows", "assets", "analytics"]);

/** Whether the shell should render a panel for this section given brand selection. */
export function shellPanelAvailable(section: WorkspaceSection, hasBrand: boolean): boolean {
  if (!SHELL_PANEL_SECTIONS.has(section)) return false;
  if (BRAND_SCOPED_PANEL_SECTIONS.has(section) && !hasBrand) return false;
  return true;
}

/** True when a route is an in-shell work object that should open as a tab
 *  (vs. a plain navigation). Shared by WorkspacePage and ShellSidePanel. */
export function isInShellRoute(route: string): boolean {
  return routeIsInShell(route);
}

export function sectionLabel(section: WorkspaceSection): string {
  switch (section) {
    case "home":
      return "Workspace home";
    case "chat":
      return "Agent";
    case "docs":
      return "Brand Docs";
    case "assets":
      return "Assets";
    case "tasks":
      return "Actions";
    case "activity":
      return "Activity";
    case "workflows":
      return "Operations";
    case "analytics":
      return "Analytics";
    default:
      return "Workspace";
  }
}
